import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useVoucher } from './VoucherContext';
import logger from '../utils/logger';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Always call useVoucher hook unconditionally (React Hooks rules)
  // Since VoucherProvider wraps NotificationProvider in App.js, this should always work
  const voucherContext = useVoucher();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const lastPathRef = useRef(window.location.pathname);
  const processedVouchersRef = useRef(new Set()); // Track processed voucher codes

  // Fetch notifications with throttling to prevent excessive API calls
  const fetchNotifications = useCallback(async (force = false) => {
    // Double-check authentication and token before making API calls
    const token = localStorage.getItem('accessToken');
    if (!isAuthenticated || !token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Throttle: Don't fetch if last fetch was less than 1 second ago (unless forced)
    const now = Date.now();
    if (!force && now - lastFetchTimeRef.current < 1000) {
      return;
    }
    lastFetchTimeRef.current = now;

    try {
      setLoading(true);
      setError(null);
      
      const [notificationsResponse, countResponse] = await Promise.all([
        notificationAPI.getNotifications(null, 50),
        notificationAPI.getUnreadCount()
      ]);

      // Handle response - API interceptor returns response.data directly
      let fetchedNotifications = [];
      if (notificationsResponse?.success && notificationsResponse?.data?.notifications) {
        // Standard format: { success: true, data: { notifications: [...] } }
        fetchedNotifications = notificationsResponse.data.notifications;
      } else if (Array.isArray(notificationsResponse?.notifications)) {
        // Direct format: { notifications: [...] }
        fetchedNotifications = notificationsResponse.notifications;
      } else if (Array.isArray(notificationsResponse)) {
        // Array format: [...]
        fetchedNotifications = notificationsResponse;
      }
      
      setNotifications(fetchedNotifications);

      // Extract and add vouchers from notifications (for referral rewards)
      // Only if VoucherProvider is available - get fresh values each time
      if (voucherContext && voucherContext.addVoucher) {
        const currentVouchers = voucherContext.vouchers || [];
        fetchedNotifications.forEach(notification => {
          if (notification.voucher && notification.voucher.code) {
            const voucherCode = notification.voucher.code;
            // Check if we've already processed this voucher
            if (!processedVouchersRef.current.has(voucherCode)) {
              // Check if voucher already exists in vouchers (use current state)
              const voucherExists = currentVouchers.some(v => v.code === voucherCode);
              if (!voucherExists) {
                try {
                  // Generate unique ID if not present
                  const voucherToAdd = {
                    ...notification.voucher,
                    id: notification.voucher.id || Date.now() + Math.random()
                  };
                  voucherContext.addVoucher(voucherToAdd);
                  processedVouchersRef.current.add(voucherCode);
                  logger.debug(`✅ Added referral voucher from notification: ${voucherCode}`);
                } catch (voucherError) {
                  logger.error('Failed to add voucher from notification:', voucherError);
                }
              } else {
                processedVouchersRef.current.add(voucherCode);
              }
            }
          }
        });
      }

      // Handle count response - API interceptor returns response.data directly
      let count = 0;
      if (countResponse?.success && countResponse?.data?.count !== undefined) {
        // Standard format: { success: true, data: { count: X } }
        count = countResponse.data.count;
      } else if (typeof countResponse?.count === 'number') {
        // Direct format: { count: X }
        count = countResponse.count;
      } else if (typeof countResponse === 'number') {
        // Direct number
        count = countResponse;
      }
      
      setUnreadCount(count);
      
      // Only log if there are notifications or unread count changes (to reduce spam)
      if (force && (fetchedNotifications.length > 0 || count > 0)) {
        logger.debug(`🔔 Notifications: ${fetchedNotifications.length} total, ${count} unread`);
      }
    } catch (err) {
      // Suppress 401 errors when user is not authenticated (expected behavior)
      const isUnauthorized = err?.status === 401 || err?.code === 'UNAUTHORIZED' || 
                            err?.response?.status === 401 || err?.response?.data?.code === 'UNAUTHORIZED';
      
      if (isUnauthorized) {
        // User is not authenticated or token is invalid - clear notifications silently
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      
      // Log other errors
      logger.error('❌ Error fetching notifications:', err);
      logger.error('   Error details:', err.response?.data || err.message);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, voucherContext]);

  // Mark notification as read - Optimistic update
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update - update UI immediately
    const notification = notifications.find(n => n._id === notificationId);
    const wasUnread = notification && !notification.read;
    
    if (wasUnread) {
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId 
            ? { ...notif, read: true, readAt: new Date() }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    // Make API call in background
    try {
      const response = await notificationAPI.markAsRead(notificationId);
      // Verify success, but don't update again if optimistic update already worked
      if (!response?.success && !response?.data?.success) {
        // Revert optimistic update on failure
        if (wasUnread) {
          setNotifications(prev => 
            prev.map(notif => 
              notif._id === notificationId 
                ? { ...notif, read: false, readAt: undefined }
                : notif
            )
          );
          setUnreadCount(prev => prev + 1);
        }
        logger.error('Failed to mark notification as read');
      }
    } catch (err) {
      logger.error('Error marking notification as read:', err);
      // Revert optimistic update on error
      if (wasUnread) {
        setNotifications(prev => 
          prev.map(notif => 
            notif._id === notificationId 
              ? { ...notif, read: false, readAt: undefined }
              : notif
            )
        );
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications]);

  // Mark all notifications as read - Optimistic update
  const markAllAsRead = useCallback(async () => {
    // Optimistic update - update UI immediately
    const previousUnreadCount = unreadCount;
    const previousNotifications = [...notifications];
    
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true, readAt: new Date() }))
    );
    setUnreadCount(0);
    
    // Make API call in background
    try {
      const response = await notificationAPI.markAllAsRead();
      // Verify success, but don't update again if optimistic update already worked
      if (!response?.success && !response?.data?.success) {
        // Revert optimistic update on failure
        setNotifications(previousNotifications);
        setUnreadCount(previousUnreadCount);
        logger.error('Failed to mark all notifications as read');
      }
    } catch (err) {
      logger.error('Error marking all notifications as read:', err);
      // Revert optimistic update on error
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
    }
  }, [notifications, unreadCount]);

  // Delete notification - Optimistic update
  const deleteNotification = useCallback(async (notificationId) => {
    // Optimistic update - update UI immediately
    const notification = notifications.find(n => n._id === notificationId);
    const wasUnread = notification && !notification.read;
    const previousNotifications = [...notifications];
    
    setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    // Make API call in background
    try {
      const response = await notificationAPI.deleteNotification(notificationId);
      // Verify success, but don't update again if optimistic update already worked
      if (!response?.success && !response?.data?.success) {
        // Revert optimistic update on failure
        setNotifications(previousNotifications);
        if (wasUnread) {
          setUnreadCount(prev => prev + 1);
        }
        logger.error('Failed to delete notification');
      }
    } catch (err) {
      logger.error('Error deleting notification:', err);
      // Revert optimistic update on error
      setNotifications(previousNotifications);
      if (wasUnread) {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [notifications]);

  // Initial fetch and periodic refresh with improved real-time updates
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(true); // Force initial fetch
      
      // Refresh notifications every 3 seconds for near real-time updates
      intervalRef.current = setInterval(() => {
        if (isAuthenticated) {
          fetchNotifications();
        }
      }, 3000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setNotifications([]);
      setUnreadCount(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [fetchNotifications, isAuthenticated]);

  // Fetch notifications when route changes (user navigates to different page)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check for route changes periodically
    const checkRouteChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath;
        fetchNotifications(true);
      }
    };

    // Check immediately
    checkRouteChange();

    // Check every second for route changes
    const routeCheckInterval = setInterval(checkRouteChange, 1000);

    // Also listen to popstate (back/forward button)
    const handlePopState = () => {
      setTimeout(() => {
        checkRouteChange();
      }, 100);
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      clearInterval(routeCheckInterval);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated, fetchNotifications]);

  // Fetch notifications when page becomes visible (user switches back to tab)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, fetchNotifications]);

  // Fetch notifications when window regains focus
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleFocus = () => {
      fetchNotifications(true);
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

